/**
 * rp-pdp-gallery — the PDP gallery scroller. U3.
 *
 * Replaces Horizon's <slideshow-component> on this surface (ADR-006: the file is
 * not edited, it is simply no longer called). It exists because the approved
 * controls in docs/canvas/Product.dc.html are three bars and two text arrows,
 * and slideshow.js's DOM contract is built around arrows, autoplay, peek,
 * slide-in modes and pause-on-hover — none of which this design has.
 *
 * WHAT IT DOES, AND NOTHING ELSE:
 *   - scrolls the track by exactly one slide when an arrow is pressed
 *   - marks the bar for the slide currently in view
 *   - disables the arrow at each end
 *
 * No autoplay, no loop, no drag handler, no keyboard handler. The track is a
 * native scroll-snap container with tabindex="0", so swipe, trackpad, and the
 * arrow keys all work without this file — which is why every read below is
 * guarded and the gallery stays usable if the module never loads.
 *
 * Position is derived from scrollLeft rather than tracked in a variable: a
 * native swipe moves the scroller without telling us, so any index we held
 * would drift out of step with what is on screen after the first flick.
 */

class RpPdpGallery extends HTMLElement {
  connectedCallback() {
    // Re-read on every connect, not once. The product section re-renders through
    // the Section Rendering API on a variant change and this element arrives
    // with a new set of slides; caching them in a constructor would leave the
    // bars pointing at the previous variant's media.
    this.track = this.querySelector('[data-rp-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-rp-slide]'));
    this.barGroups = Array.from(this.querySelectorAll('[data-rp-bars]'));
    this.prev = this.querySelector('[data-rp-prev]');
    this.next = this.querySelector('[data-rp-next]');

    if (this.slides.length < 2) return;

    this.pending = null;
    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);

    // `passive` because this listener never calls preventDefault; letting the
    // browser know keeps scrolling off the main thread's critical path.
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('scrollend', this.onScrollEnd);
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('scrollend', this.onScrollEnd);
    cancelAnimationFrame(this.frame);
    clearTimeout(this.releaseTimer);
  }

  onScrollEnd() {
    this.pending = null;
    this.sync();
  }

  /** Index of the slide nearest the left edge of the viewport. */
  get index() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  }

  onScroll() {
    // Coalesce to one update per frame. A scroll event can fire dozens of times
    // per flick and every handler here touches layout.
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.sync());
  }

  step(direction) {
    // ⚠️ STEP FROM THE PENDING TARGET, NOT FROM LIVE scrollLeft.
    // Measured in the browser: three `next` clicks in quick succession left the
    // gallery on slide 2 of 4 and the following `prev` appeared to do nothing.
    // Cause — `index` reads scrollLeft, which during a smooth scroll is a
    // fraction of the way to the previous target, so a click that lands
    // mid-animation rounds back to the slide being left behind and re-issues
    // the same scroll. Tapping an arrow twice quickly is ordinary behaviour, so
    // this is a real defect and not a test artefact.
    const base = this.pending ?? this.index;
    const target = Math.min(Math.max(base + direction, 0), this.slides.length - 1);
    this.pending = target;

    // `scrollTo` rather than `scrollIntoView`: scrollIntoView on a horizontal
    // scroller also scrolls the PAGE to bring the gallery into view, which yanks
    // the viewport on a click the shopper made while reading further down.
    this.track.scrollTo({
      left: target * this.track.clientWidth,
      behavior: this.motionOk ? 'smooth' : 'auto',
    });

    // Release the lock once the scroll settles, so a native swipe is tracked
    // again. `scrollend` fires after both programmatic and user scrolling; the
    // timeout is the fallback for browsers that do not implement it, and is
    // harmless where they do because clearing an already-cleared lock is a
    // no-op.
    clearTimeout(this.releaseTimer);
    this.releaseTimer = setTimeout(() => { this.pending = null; }, 700);
  }

  /**
   * The theme's motion dial, not a second detection site. AGENTS.md caps the
   * theme at exactly two `prefers-reduced-motion` media queries and both are
   * already spent, so this reads the `data-motion` attribute src/app.css drives
   * them through instead of adding a third.
   */
  get motionOk() {
    return document.documentElement.dataset.motion !== 'off';
  }

  sync() {
    const index = this.index;
    const last = this.slides.length - 1;

    this.barGroups.forEach((group) => {
      Array.from(group.children).forEach((bar, i) => {
        bar.toggleAttribute('data-rp-current', i === index);
      });
    });

    // Disabled at the ends rather than wrapping: the artboard shows a linear
    // strip of bars, which is a finite set with a beginning and an end.
    if (this.prev) this.prev.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= last;
  }
}

if (!customElements.get('rp-pdp-gallery')) {
  customElements.define('rp-pdp-gallery', RpPdpGallery);
}
